import { EVALUATION_TOOL } from '../constant';
import { EXECUTION_STATUS } from '../hooks/useReplayExecution';
import {
    isRecordingSkipMarkerStep,
    isStepSkippedInRecordedReplay,
    mapRecordingStatusForDisplay,
    shouldSuppressRecordedOutcomeOnIdlePlan,
    shouldSuppressRecordingOutcomeDuringReplay,
    STEP_RECORDING_STATUS,
} from './replayEligibility';

function normalizeEffectiveStepOptions(options) {
    if (typeof options === 'boolean') {
        return { isReplayFlow: options, isSavedPlanIdle: false, replayStatus: null, instructions: null };
    }
    return {
        isReplayFlow: false,
        isSavedPlanIdle: false,
        replayStatus: null,
        instructions: null,
        ...options,
    };
}

/**
 * Pass/fail for `evaluateTestStep` — single module shared across Automation Studio.
 *
 * **UX reference:** the left **Instructions & Steps** tree (`InstructionHierarchy` → `StepItemInner`)
 * is the source of truth for green check vs red X, replay borders, and when to show AI %.
 * Step editor, verification tab, and running execution views must use these exports so they
 * never disagree with that tree.
 *
 * **Data reference:** Script Manager replay panels use `stepDetails.result.result` (passed,
 * assertionPassed, success); we merge the same shape via optional `execDetail`.
 */

/** True when this step is `evaluateTestStep` on the step or on executionGroup.mainAction. */
export function isEvaluationStepTool(step) {
    return (
        step?.tool === EVALUATION_TOOL ||
        step?.executionGroup?.mainAction?.tool === EVALUATION_TOOL
    );
}

/**
 * Nested evaluation blob (Script Manager: stepDetails.result.result, replay: execDetail.result.result).
 * Prefer **execDetail** when both exist — plan `step.result.result` can be stale after a new replay.
 * @param {object|null|undefined} step
 * @param {object|null|undefined} execDetail optional replay/live stepDetails[i]
 * @returns {object|null}
 */
function getNestedEvaluationResult(step, execDetail = null) {
    return execDetail?.result?.result ?? step?.result?.result ?? null;
}

/**
 * Merge live/replay execDetail into step so verdict reads the same as Script Manager (result.result).
 */
export function mergeExecDetailIntoEvaluationStep(step, execDetail) {
    if (!step) return null;
    if (!execDetail?.result) return step;
    const inner = execDetail.result.result;
    return {
        ...step,
        passed: step.passed ?? inner?.passed,
        result: {
            ...(step.result || {}),
            result: step.result?.result ?? inner,
        },
    };
}

/**
 * AI / assertion verdict for evaluateTestStep — aligns with Script Manager result.result shape
 * (passed, assertionPassed, success) and optional replay execDetail merge.
 *
 * @param {object|null|undefined} step
 * @param {object|null|undefined} execDetail
 * @returns {boolean|null} null = inconclusive / pending
 */
export function getEvaluationVerdict(step, execDetail = null) {
    if (!step) return null;
    if (!isEvaluationStepTool(step)) return null;

    // Skipped steps have no verdict. Without this guard, stale prior-run `passed`
    // values in step.result?.result, step.extractedData, step.executionGroup, etc.
    // leak through the candidates list and produce a false true/false verdict.
    if (step.status === 'skipped') return null;

    // Queued steps must not inherit stale plan/last-run verdict fields.
    if (step.status === 'pending') return null;

    const rr = getNestedEvaluationResult(step, execDetail);

    const candidates = [
        step.passed,
        step.details?.passed,
        step.args?.passed,
        step.result?.passed,
        rr?.passed,
        rr?.assertionPassed,
        step.extractedData?.evaluation?.passed,
        step.executionGroup?.mainAction?.passed,
        step.executionGroup?.mainAction?.args?.passed,
        step.executionGroup?.mainAction?.details?.passed,
    ];

    if (candidates.some((c) => c === false || c === 'false')) return false;
    if (candidates.some((c) => c === true || c === 'true')) return true;

    // Script Manager: evaluationResult.success false means step failed
    if (rr?.success === false) return false;
    // Legacy: only success with no explicit passed — treat as passed (ResultsPanel OR pattern)
    if (rr?.success === true) return true;

    return null;
}

/**
 * Effective status for badges / sidebar (evaluateTestStep uses verdict, not raw step.status).
 * @param {boolean|object} [options=false] isReplayFlow boolean legacy, or { isReplayFlow, isSavedPlanIdle }
 */
export function getEffectiveStepStatus(step, execDetail = null, instructions = null, options = false) {
    if (!step) return 'pending';

    const { isReplayFlow, isSavedPlanIdle, replayStatus } = normalizeEffectiveStepOptions(options);

    // Skipped wins over verdict — short-circuit before any candidate walk so
    // stale prior-run signals can't override a skipped state.
    if (step.status === 'skipped') return 'skipped';

    if (isSavedPlanIdle) {
        if (isRecordingSkipMarkerStep(step, instructions)) return 'skipped';
        if (shouldSuppressRecordedOutcomeOnIdlePlan(step, instructions, true)) return 'pending';
    }

    if (isReplayFlow && replayStatus === EXECUTION_STATUS.FAILED) {
        return 'failed';
    }

    if (
        isReplayFlow &&
        instructions &&
        replayStatus !== EXECUTION_STATUS.PASSED &&
        isStepSkippedInRecordedReplay(step, instructions, { isReplayFlow: true, replayStatus })
    ) {
        return 'skipped';
    }

    if (replayStatus === EXECUTION_STATUS.IN_PROGRESS) {
        return 'running';
    }

    if (
        isReplayFlow &&
        instructions &&
        shouldSuppressRecordingOutcomeDuringReplay(step, instructions, replayStatus, { isReplayFlow })
    ) {
        return 'pending';
    }

    const displayStatus = mapRecordingStatusForDisplay(step.status);

    if (!isEvaluationStepTool(step)) {
        if (step.executionGroup?.mainAction?.status === 'failed') return 'failed';
        return displayStatus || 'pending';
    }

    const v = getEvaluationVerdict(step, execDetail);
    if (v === false) return 'failed';
    if (v === true) return 'success';

    const raw = displayStatus || 'pending';
    if (raw === 'failed') return 'failed';
    if (step.executionGroup?.mainAction?.status === 'failed') return 'failed';

    const rr = getNestedEvaluationResult(step, execDetail);
    if (rr?.success === false) return 'failed';
    if ((raw === 'success' || raw === STEP_RECORDING_STATUS.COMPLETED) && rr?.success === true) return 'success';
    if (raw === 'success' || raw === STEP_RECORDING_STATUS.COMPLETED) return 'pending';

    return raw;
}

/**
 * Replay border status: Passed + verdict false → Failed.
 * @param {boolean|object} [replayOptions=false]
 */
export function getAdjustedReplayStatus(step, replayStatus, execDetail = null, instructions = null, replayOptions = false) {
    const { isReplayFlow, isSavedPlanIdle } = normalizeEffectiveStepOptions(replayOptions);

    if (isSavedPlanIdle) {
        // No replay-status chrome before the user starts replay (skip rows use isRecordingSkipStep styling only).
        return null;
    }

    if (isReplayFlow && instructions && isRecordingSkipMarkerStep(step, instructions)) {
        if (replayStatus === EXECUTION_STATUS.FAILED) return EXECUTION_STATUS.FAILED;
        if (replayStatus === EXECUTION_STATUS.PASSED) return EXECUTION_STATUS.PASSED;
        return EXECUTION_STATUS.SKIPPED;
    }

    if (
        isReplayFlow &&
        instructions &&
        isStepSkippedInRecordedReplay(step, instructions, { isReplayFlow: true }) &&
        (!replayStatus || replayStatus === EXECUTION_STATUS.NEW || replayStatus === EXECUTION_STATUS.PENDING)
    ) {
        return EXECUTION_STATUS.SKIPPED;
    }
    if (!replayStatus || replayStatus === EXECUTION_STATUS.NEW) return replayStatus;
    if (isEvaluationStepTool(step) && replayStatus === EXECUTION_STATUS.PASSED) {
        const verdict = getEvaluationVerdict(step, execDetail);
        if (verdict === false) return EXECUTION_STATUS.FAILED;
    }
    return replayStatus;
}

/**
 * Verification tab + metadata: pass/fail must match getEvaluationVerdict (no "null + success → pass").
 * @param {boolean|object} [options=false] same shape as getEffectiveStepStatus options
 */
export function getVerificationTabState(step, execDetail = null, options = false) {
    const { isReplayFlow, isSavedPlanIdle, replayStatus, instructions } = normalizeEffectiveStepOptions(options);

    const suppressStaleOutcome =
        (isSavedPlanIdle && !isRecordingSkipMarkerStep(step, instructions)) ||
        shouldSuppressRecordingOutcomeDuringReplay(step, instructions, replayStatus, { isReplayFlow });

    if (suppressStaleOutcome && step?.status !== 'skipped') {
        return {
            hasBeenExecuted: false,
            isPassed: false,
            isEvaluationTool: isEvaluationStepTool(step),
            effectiveStatus: 'pending',
        };
    }

    const isSkipped =
        step?.status === 'skipped' || step?.executionGroup?.mainAction?.status === 'skipped';

    const rawExecStatus = execDetail?.status ?? execDetail?.result?.status;
    const execStatusNorm =
        rawExecStatus != null && rawExecStatus !== ''
            ? String(rawExecStatus).toLowerCase()
            : '';
    // Terminal replay payload (exclude skipped — not a completed run with outcome to show).
    const replayStepTerminal =
        execDetail &&
        execStatusNorm !== 'skipped' &&
        ['success', 'failed', 'completed', 'passed'].includes(execStatusNorm);

    const replayStepRunning =
        execDetail &&
        ['in-progress', 'running'].includes(execStatusNorm);

    const isRunning =
        !isSkipped &&
        (replayStepRunning ||
            step?.status === 'running' ||
            step?.executionGroup?.mainAction?.status === 'running' ||
            replayStatus === EXECUTION_STATUS.IN_PROGRESS);

    const hasPlanExecutionHistory =
        step?.status === 'success' ||
        step?.status === 'failed' ||
        step?.status === 'completed' ||
        typeof step?.passed === 'boolean' ||
        (step?.executionGroup?.mainAction &&
            (step.executionGroup.mainAction.status === 'success' ||
                step.executionGroup.mainAction.status === 'failed' ||
                step.executionGroup.mainAction.status === 'completed' ||
                typeof step.executionGroup.mainAction.passed === 'boolean'));

    const replayHasTerminalOutcome = Boolean(
        replayStatus === EXECUTION_STATUS.PASSED ||
        replayStatus === EXECUTION_STATUS.FAILED ||
        replayStatus === EXECUTION_STATUS.SKIPPED ||
        replayStepTerminal
    );

    // Replay: only show verification outcomes after this replay run finishes the step.
    // Generate/record: keep legacy behaviour (running counts as executed for live UI).
    const hasBeenExecuted = isReplayFlow
        ? !isSkipped && replayHasTerminalOutcome
        : !isSkipped && (isRunning || replayStepTerminal || hasPlanExecutionHistory);

    const isEvaluationTool = isEvaluationStepTool(step);
    const evalVerdict = isEvaluationTool ? getEvaluationVerdict(step, execDetail) : null;

    const explicitFailed =
        step?.status === 'failed' ||
        step?.executionGroup?.mainAction?.status === 'failed' ||
        (isEvaluationTool && evalVerdict === false);

    const isPassed =
        hasBeenExecuted &&
        !isRunning &&
        !explicitFailed &&
        (isEvaluationTool ? evalVerdict === true : step?.status === 'success' ||
            step?.status === 'completed' ||
            step?.passed === true ||
            step?.executionGroup?.mainAction?.passed === true);

    return {
        hasBeenExecuted,
        isPassed,
        isRunning,
        isEvaluationTool,
        effectiveStatus: getEffectiveStepStatus(step, execDetail, instructions, options),
    };
}

/**
 * Running execution card: title line for the current step.
 *
 * - **Replay (`execDetail` from live replay):** prefer WebSocket step payload, then plan step fields.
 * - **Generate AI (`execDetail` should be null):** use only `step` (from `instructions` aiSteps) so prior
 *   replay data in Redux cannot override the generated step description.
 *
 * Plan JSON often stores copy in `extractedData.stepDescription` only; instruction tree uses `description`.
 */
export function getReplayStepDisplayDescription(step, execDetail = null) {
    const trim = (v) => (v == null ? '' : String(v).trim());

    const fromExec =
        execDetail?.stepDescription ??
        execDetail?.description ??
        execDetail?.extractedData?.stepDescription ??
        execDetail?.extractedData?.description;
    const execText = trim(fromExec);

    const args = execDetail?.args && typeof execDetail.args === 'object' ? execDetail.args : null;
    if (args?.description != null && trim(args.description)) return trim(args.description);
    if (args?.url != null && trim(args.url) && execDetail?.tool === 'browser_navigate') {
        return `Navigating to ${trim(args.url)}`;
    }

    if (!step) return trim(execDetail?.tool) || '';

    const fromStep =
        step.description ??
        step.extractedData?.stepDescription ??
        step.extractedData?.description;
    const stepText = trim(fromStep);

    // Prefer plan text when execution description was masked.
    // Some replay payloads include `********` in action descriptions (e.g. typing a secret),
    // but the plan stores canonical tokens which should be propagated for display.
    if (execText && /\*{4,}/.test(execText) && stepText) return stepText;
    if (execText) return execText;
    if (stepText) return stepText;

    return trim(step.tool) || '';
}

const firstNonEmptyString = (...vals) => {
    for (const v of vals) {
        if (v == null) continue;
        const s = typeof v === 'string' ? v.trim() : String(v).trim();
        if (s) return s;
    }
    return null;
};

/**
 * Replay WebSocket payloads may set `error` / `errorMessage` to transport/LangGraph diagnostics
 * (wrong graph node id). Prefer evaluation / LLM copy when present — matches Script Manager
 * `evaluationResult` fields before raw `error`.
 *
 * @param {object|null|undefined} execDetail stepDetails[i]
 * @returns {string|null}
 */
export function getReplayUserFacingErrorMessage(execDetail) {
    if (!execDetail || typeof execDetail !== 'object') return null;

    const replayResult = execDetail.result;
    const replayEval = replayResult?.result;
    const r = replayEval ?? replayResult ?? execDetail;
    const summaryIfFailed = (obj) => {
        if (!obj || typeof obj !== 'object' || typeof obj.summary !== 'string') return null;
        if (obj.success === false) return obj.summary.trim() || null;
        if (String(obj.status ?? '').trim().toLowerCase() === 'failed') return obj.summary.trim() || null;
        return null;
    };

    const text = firstNonEmptyString(
        replayEval?.failureReason,
        replayEval?.failure_reason,
        r?.failureReason,
        r?.failure_reason,
        replayEval?.reason,
        r?.reason,
        typeof replayEval?.llmReason === 'string' ? replayEval.llmReason : null,
        typeof r?.llmReason === 'string' ? r.llmReason : null,
        typeof replayEval?.message === 'string' ? replayEval.message : null,
        typeof r?.message === 'string' ? r.message : null,
        summaryIfFailed(replayEval),
        summaryIfFailed(r),
        summaryIfFailed(replayResult),
        replayResult?.details?.reason,
        replayEval?.details?.reason,
        execDetail?.result?.details?.reason
    );
    if (text) return text;

    const err =
        replayEval?.errorMessage ??
        replayEval?.error ??
        r?.errorMessage ??
        r?.error ??
        replayResult?.errorMessage ??
        replayResult?.error;
    if (typeof err === 'string' && err.trim()) return err.trim();
    if (err && typeof err === 'object' && err.message) return String(err.message).trim();

    if (typeof execDetail.error === 'string' && execDetail.error.trim()) return execDetail.error.trim();
    if (execDetail.error && typeof execDetail.error === 'object' && execDetail.error.message) {
        return String(execDetail.error.message).trim();
    }

    const events = execDetail.stepWiseDynamicResults;
    if (Array.isArray(events)) {
        for (let i = events.length - 1; i >= 0; i--) {
            const e = events[i];
            if (e?.type !== 'tool_call' || e?.tool !== 'finalize_dynamic_step') continue;
            const s = summaryIfFailed(e.args);
            if (s) return s;
        }
    }

    return null;
}

/** Tool name from replay step payload when present (avoids stale plan step.tool during replay). */
export function getReplayStepToolForDisplay(execDetail, planStep) {
    const t = execDetail?.tool ?? execDetail?.result?.tool ?? planStep?.tool;
    return t != null && String(t).trim() ? String(t).trim() : null;
}

/**
 * AI evaluation narrative for `evaluateTestStep` from replay `stepDetails[i]` (same shape as StepEditorView:
 * `replayResult = detail.result`, `replayEval = replayResult.result`).
 * Includes llmReason and failure* fields — not only `reason`.
 */
export function getEvaluationNarrativeFromExecDetail(execDetail) {
    if (!execDetail || typeof execDetail !== 'object') return null;

    const replayResult = execDetail.result;
    const replayEval = replayResult?.result;
    const r = replayEval ?? replayResult ?? execDetail;

    const narrative = firstNonEmptyString(
        replayEval?.llmReason,
        replayEval?.reason,
        r?.reason,
        r?.failureReason,
        r?.failure_reason,
        replayEval?.failureReason,
        replayEval?.failure_reason,
        typeof replayEval?.message === 'string' ? replayEval.message : null,
        typeof r?.message === 'string' ? r.message : null,
        replayResult?.details?.reason,
        replayEval?.details?.reason,
        execDetail?.result?.details?.reason,
        execDetail?.result?.result?.reason,
        execDetail?.result?.result?.llmReason,
        execDetail?.reason
    );

    if (narrative) return narrative;

    const err =
        replayEval?.errorMessage ??
        replayEval?.error ??
        r?.errorMessage ??
        r?.error;
    if (typeof err === 'string' && err.trim()) return err.trim();
    if (err && typeof err === 'object' && err.message) return String(err.message).trim();

    return null;
}

/** Generate AI / plan step fallbacks when not using replay stepDetails only. */
export function getEvaluationNarrativeFromPlanStep(step) {
    if (!step || typeof step !== 'object') return null;
    const inner = step.result?.result ?? step.result;
    return firstNonEmptyString(
        inner?.llmReason,
        inner?.reason,
        step.reason,
        step.details?.reason,
        inner?.failureReason,
        inner?.failure_reason,
        step.failureReason,
        step.failure_reason,
        step.args?.reason,
        step.arguments?.reason,
        step.executionGroup?.mainAction?.reason,
        step.executionGroup?.mainAction?.details?.reason
    );
}
